import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
    DatePicker,
    DaSelect,
    InternalGrid,
    Text,
    ScheduleIcon,
    DaButton,
    HeartIcon,
    Box,
    Card,
} from '@paygreen/paygreen-ui';
import { useUser } from '../../context';
import dayjs from '../../utils/dayjsHelper';
import { startOfMonth, endOfMonth } from '../../utils/constants';
import { IsMobile } from '../../utils/tools';

const DashboardFilter = ({
    t,
    setEndDate,
    setStartDate,
    endDate,
    startDate,
    partnershipsData,
    setSelectorId,
}) => {
    const { user } = useUser();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const searchStartDate = searchParams.get('startDate');
    const searchEndDate = searchParams.get('endDate');

    // we apply as the minimum startDate the first day of the month of subscription of the user
    const minimumStartDate =
        user.createdAt &&
        dayjs(new Date(user.createdAt * 1000)).format('01/MM/YYYY');

    // we create selector for all partnerships availables for user as SHOP or ASSOCIATION
    let partnershipOptions = [{ value: '', text: t('admin.charities.title') }];

    if (user && user.role === 'ASSOCIATION') {
        partnershipsData &&
            partnershipsData.forEach((option) => {
                const { idUser, idAccount } = option;

                partnershipOptions.push({
                    value: idUser,
                    text: idAccount,
                });
            });
    } else {
        partnershipsData &&
            partnershipsData.forEach((option) => {
                const { idAssociation, name } = option.association;

                partnershipOptions.push({
                    value: idAssociation,
                    text: name,
                });
            });
    }

    const getPartnerName = (id) => {
        const tempData = partnershipOptions.filter((item) => item.value === id);
        return tempData[0].text;
    };

    const handleStartDate = (date) => {
        setStartDate(date);
    };

    const handleEndDate = (date) => {
        setEndDate(date);
    };

    const handleSelect = (e) => {
        const { value } = e.target;

        setSelectorId({
            id: value,
            name: getPartnerName(value),
        });
    };

    return (
        <Card
            colorWab="white10"
            shadowSize="none"
            paddingLateral="xs"
            paddingTop="xs"
            radiusSize="sm"
            blockWidth="none"
        >
            <InternalGrid
                alignItems="center"
                justifyContent="flex-start"
                hasStaticWidth={false}
                childrenMarginBottom={IsMobile() ? 'sm' : 'md'}
            >
                <InternalGrid
                    displayType="grid"
                    gridTemplateColumns="auto auto"
                    gridGap="md"
                    alignItems="center"
                >
                    <Text textSize="xs">{t('datePicker.from')}</Text>

                    <Box marginRight="sm">
                        <DatePicker
                            locale={t('datePicker.locale')}
                            id="startDate"
                            minimumDate={minimumStartDate}
                            maximumDate={endDate || endOfMonth}
                            fieldSize="sm"
                            blockWidth="sm"
                            resetDate={endDate}
                            value={startDate || startOfMonth}
                            onChange={handleStartDate}
                            icon={<ScheduleIcon />}
                        />
                    </Box>
                </InternalGrid>

                <InternalGrid
                    displayType="grid"
                    gridTemplateColumns="auto auto"
                    gridGap="md"
                    alignItems="center"
                >
                    <Text textSize="xs">{t('datePicker.to')}</Text>

                    <Box marginRight="sm">
                        <DatePicker
                            locale={t('datePicker.locale')}
                            id="endDate"
                            minimumDate={startDate || startOfMonth}
                            maximumDate={endOfMonth}
                            fieldSize="sm"
                            blockWidth="sm"
                            resetDate={startDate}
                            value={endDate || endOfMonth}
                            onChange={handleEndDate}
                            icon={<ScheduleIcon />}
                        />
                    </Box>
                </InternalGrid>

                <InternalGrid
                    displayType="grid"
                    gridTemplateColumns="auto auto"
                    gridGap="md"
                    alignItems="center"
                >
                    <Text textSize="xs">{t('dashboard.for')}</Text>

                    <Box marginRight="md">
                        <DaSelect
                            id="partnerSelector"
                            blockWidth="md"
                            fieldSize="sm"
                            options={partnershipOptions && partnershipOptions}
                            onChange={handleSelect}
                        />
                    </Box>
                </InternalGrid>

                {!IsMobile() && (searchEndDate || searchStartDate) ? (
                    <Link to={`/donations?${searchParams.toString()}`}>
                        <DaButton
                            buttonSize="sm"
                            buttonStyle="line"
                            gradient="theme"
                            colorTheme="quaternary"
                            icon={<HeartIcon />}
                        >
                            {t('dashboard.donationsLink')}
                        </DaButton>
                    </Link>
                ) : null}
            </InternalGrid>
        </Card>
    );
};

DashboardFilter.propTypes = {
    setEndDate: PropTypes.func.isRequired,
    setStartDate: PropTypes.func.isRequired,
    endDate: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    /**
     * data received from API to build selector options values
     */
    partnershipsData: PropTypes.array,
    setSelectorId: PropTypes.func.isRequired,
};

export default withTranslation('common')(DashboardFilter);
